import { useState, useEffect, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useUserContext } from '../../hooks/useUserContext';
import tenantService from '../../services/tenantService';

// Mirrors the server allowlist and limit in TenantController.UploadLogo.
const ALLOWED_LOGO_EXTENSIONS = ['png', 'jpg', 'jpeg', 'svg'];
const LOGO_ACCEPT = '.png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml';
const MAX_LOGO_BYTES = 5 * 1024 * 1024;

function formatMegabytes(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

export default function TenantSettings() {
  const { backendProfile, loading: contextLoading } = useUserContext();
  const tenantId = backendProfile?.tenantId || '';

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [snackMessage, setSnackMessage] = useState<string | null>(null);

  // Name: what the server has vs what the field holds.
  const [savedName, setSavedName] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);

  // Logo: what the server has vs what is staged (a new file, or removal).
  const [savedLogoUrl, setSavedLogoUrl] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoFilePreview, setLogoFilePreview] = useState<string | null>(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const nameChanged = tenantName.trim() !== savedName;
  const logoChanged = logoFile !== null || removeLogo;
  const isDirty = nameChanged || logoChanged;

  useEffect(() => {
    if (tenantId && !contextLoading) {
      void loadTenantData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, contextLoading]);

  // Closing the tab is the one exit a staged logo cannot survive.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const loadTenantData = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const { tenant, logoUrl } = await tenantService.getTenantWithLogo(tenantId);
      const name = tenant?.name || '';
      setSavedName(name);
      setTenantName(name);
      setSavedLogoUrl(logoUrl);
    } catch (error: unknown) {
      console.error('Error loading tenant data:', error);
      setLoadError("Couldn't load your organization's settings.");
    } finally {
      setLoading(false);
    }
  };

  const clearStagedLogo = () => {
    setLogoFile(null);
    setLogoFilePreview(null);
    setRemoveLogo(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so picking the same file again after a rejection still fires onChange.
    e.target.value = '';
    if (!file) return;

    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!ALLOWED_LOGO_EXTENSIONS.includes(extension)) {
      setLogoError(`${file.name} isn't a PNG, JPG or SVG.`);
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError(`${file.name} is ${formatMegabytes(file.size)}. The limit is 5 MB.`);
      return;
    }

    setLogoError(null);
    setRemoveLogo(false);
    setLogoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setLogoFilePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    clearStagedLogo();
    setLogoError(null);
    setRemoveLogo(true);
  };

  const handleDiscard = () => {
    setTenantName(savedName);
    setNameError(null);
    setLogoError(null);
    setSaveError(null);
    clearStagedLogo();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving || !isDirty) return;

    const trimmedName = tenantName.trim();
    if (!trimmedName) {
      setNameError("Enter your organization's name.");
      return;
    }

    setSaving(true);
    setSaveError(null);
    setLogoError(null);

    // Name and logo are separate requests with separate outcomes, so a rejected
    // logo never hides a name that did save (and vice versa).
    let nameSaved = false;
    let logoOutcome: 'saved' | 'removed' | null = null;

    if (nameChanged) {
      try {
        await tenantService.updateTenant(tenantId, { name: trimmedName });
        setSavedName(trimmedName);
        setTenantName(trimmedName);
        nameSaved = true;
      } catch (error: unknown) {
        setSaveError(errorMessage(error, "Couldn't save the organization name."));
      }
    }

    if (logoChanged) {
      try {
        if (removeLogo) {
          await tenantService.deleteTenantLogo(tenantId);
          logoOutcome = 'removed';
        } else if (logoFile) {
          await tenantService.uploadTenantLogo(tenantId, logoFile);
          logoOutcome = 'saved';
        }
        setSavedLogoUrl(await tenantService.getTenantLogo(tenantId));
      } catch (error: unknown) {
        setLogoError(errorMessage(error, "Couldn't update the logo."));
      } finally {
        // Either way the staged change is consumed: on failure the admin picks again
        // rather than re-sending the same rejected file on the next save.
        clearStagedLogo();
      }
    }

    setSaving(false);

    if (nameSaved && logoOutcome) {
      setSnackMessage('Settings saved.');
    } else if (nameSaved) {
      setSnackMessage('Organization name saved.');
    } else if (logoOutcome === 'saved') {
      setSnackMessage('Logo saved.');
    } else if (logoOutcome === 'removed') {
      setSnackMessage('Logo removed.');
    }
  };

  if (contextLoading || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!tenantId) {
    return (
      <Alert severity="error">
        Unable to load tenant information. Please try logging in again.
      </Alert>
    );
  }

  if (loadError) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => void loadTenantData()}>
            Retry
          </Button>
        }
      >
        {loadError}
      </Alert>
    );
  }

  const previewSrc = removeLogo ? null : (logoFilePreview ?? savedLogoUrl);
  const hasSavedLogo = Boolean(savedLogoUrl);

  return (
    <Box sx={{ maxWidth: 760 }}>
      {saveError && (
        <Alert severity="error" onClose={() => setSaveError(null)} sx={{ mb: 3 }}>
          {saveError}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 2 }}>
        <form onSubmit={handleSubmit} noValidate>
          <Stack spacing={4}>
            <TextField
              label="Organization name"
              fullWidth
              value={tenantName}
              onChange={(e) => {
                setTenantName(e.target.value);
                if (nameError) setNameError(null);
              }}
              error={nameError !== null}
              helperText={nameError}
              disabled={saving}
              slotProps={{ htmlInput: { maxLength: 200 } }}
            />

            <Box>
              <Typography component="h2" variant="subtitle1" fontWeight={600} id="logo-heading">
                Organization logo
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Replaces the CPAx mark in the top bar. Compact marks read best at that size.
              </Typography>

              {previewSrc && (
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      bgcolor: 'surface.sunken',
                      display: 'inline-block',
                    }}
                  >
                    <img
                      src={previewSrc}
                      alt={logoFile ? `${logoFile.name}, not saved yet` : 'Current organization logo'}
                      style={{ height: 80, width: 'auto', maxWidth: 240, objectFit: 'contain', display: 'block' }}
                    />
                  </Box>
                  {logoFile && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {logoFile.name}, not saved yet
                    </Typography>
                  )}
                </Box>
              )}

              {removeLogo && (
                <Box
                  sx={{
                    mb: 2,
                    px: 2,
                    py: 1.5,
                    borderRadius: 2,
                    bgcolor: 'tint.primarySoft',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 200 }}>
                    The logo will be removed when you save.
                  </Typography>
                  <Button size="small" onClick={clearStagedLogo} disabled={saving}>
                    Keep logo
                  </Button>
                </Box>
              )}

              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept={LOGO_ACCEPT}
                onChange={handleLogoChange}
                aria-hidden="true"
                tabIndex={-1}
              />
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button
                  variant="outlined"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={saving}
                  aria-describedby={logoError ? 'logo-error' : 'logo-hint'}
                >
                  {hasSavedLogo || logoFile ? 'Change logo' : 'Upload logo'}
                </Button>
                {hasSavedLogo && !logoFile && !removeLogo && (
                  <Button onClick={handleRemoveLogo} disabled={saving}>
                    Remove logo
                  </Button>
                )}
              </Stack>
              <FormHelperText id="logo-hint" sx={{ mt: 1 }}>
                PNG, JPG or SVG, up to 5 MB.
              </FormHelperText>
              {logoError && (
                <FormHelperText id="logo-error" error role="alert" sx={{ mt: 0.5 }}>
                  {logoError}
                </FormHelperText>
              )}
            </Box>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                type="submit"
                variant="contained"
                disabled={!isDirty || saving}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
              {isDirty && !saving && (
                <Button onClick={handleDiscard}>Discard changes</Button>
              )}
            </Stack>
          </Stack>
        </form>
      </Paper>

      <Snackbar
        open={!!snackMessage}
        autoHideDuration={5000}
        onClose={() => setSnackMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackMessage(null)} severity="success" variant="filled" sx={{ width: '100%' }}>
          {snackMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
